import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  clearAuthToken,
  readAuthToken,
  subscribeAuthTokenChanges,
  writeAuthToken,
} from "./auth-storage";
import {
  decodeImpersonationActor,
  type ImpersonationActor,
} from "./impersonation";

interface AuthTokenContextValue {
  /** Effective token: impersonation if present, else persistent. */
  token: string | undefined;
  /** True when the request is being made under an admin-impersonation token. */
  isImpersonating: boolean;
  /** Decoded actor claim if the active token is an impersonation token. */
  actor: ImpersonationActor | undefined;
  /** Persist a referrer's own session token to localStorage. */
  setToken: (token: string) => void;
  /** Clear the persistent token (logout). Does not affect impersonation. */
  clear: () => void;
  /**
   * Begin an in-memory impersonation session. Persistent localStorage token
   * is intentionally left untouched so the user's own session is preserved
   * for restoration if they exit impersonation in this tab. Closing the
   * tab also returns to persistent state since the impersonation token is
   * never written to disk.
   */
  beginImpersonation: (impersonationToken: string) => void;
  /** End impersonation; the next render falls through to the persistent token. */
  exitImpersonation: () => void;
}

const AuthTokenContext = createContext<AuthTokenContextValue | undefined>(
  undefined,
);
AuthTokenContext.displayName = "AuthTokenContext";

export function AuthTokenProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [persistentToken, setPersistentToken] = useState<string | undefined>(
    () => readAuthToken(),
  );
  const [impersonationToken, setImpersonationToken] = useState<
    string | undefined
  >(undefined);

  const setToken = useCallback((next: string) => {
    writeAuthToken(next);
    setPersistentToken(next);
  }, []);

  const clear = useCallback(() => {
    clearAuthToken();
    setPersistentToken(undefined);
  }, []);

  const beginImpersonation = useCallback((next: string) => {
    setImpersonationToken(next);
  }, []);

  const exitImpersonation = useCallback(() => {
    setImpersonationToken(undefined);
  }, []);

  // Cross-tab sync: another tab logging out (or in) should propagate
  // here so we don't keep rendering authenticated UI with a stale or
  // missing token. The handler calls back with the latest token from
  // localStorage, which has already been validated by readAuthToken.
  // Impersonation tokens are intentionally NOT cross-tab-synced — they're
  // scoped to the tab they were opened in.
  useEffect(() => {
    return subscribeAuthTokenChanges((next) => {
      setPersistentToken(next);
    });
  }, []);

  const value = useMemo<AuthTokenContextValue>(() => {
    const effective = impersonationToken ?? persistentToken;
    const actor = impersonationToken
      ? decodeImpersonationActor(impersonationToken)
      : undefined;
    return {
      token: effective,
      isImpersonating: impersonationToken !== undefined,
      actor,
      setToken,
      clear,
      beginImpersonation,
      exitImpersonation,
    };
  }, [
    persistentToken,
    impersonationToken,
    setToken,
    clear,
    beginImpersonation,
    exitImpersonation,
  ]);

  return (
    <AuthTokenContext.Provider value={value}>
      {children}
    </AuthTokenContext.Provider>
  );
}

export function useAuthToken(): AuthTokenContextValue {
  const ctx = useContext(AuthTokenContext);
  if (!ctx) {
    throw new Error("useAuthToken must be used within AuthTokenProvider");
  }
  return ctx;
}
